import React, { useState } from "react";
import {
    Card,
    CardBody,
    Col,
    Container,
    Row,
    Input,
    Label,
    Button,
    Table,
    CardHeader,
} from "reactstrap";
import Breadcrumbs from "../../../components/Common/Breadcrumb";

const IncomeReport = () => {
    // State for selected date range and search query
    const [selectedDateRange, setSelectedDateRange] = useState("");
    const [searchQuery, setSearchQuery] = useState("");

    // State to store filtered income reports
    const [filteredIncomeReports, setFilteredIncomeReports] = useState([]);

    // Dummy data for income reports
    const incomeReports = [
        {
            name: "John Doe",
            invoiceNumber: "INV001",
            incomeHead: "Tuition Fees",
            date: "2025-01-01",
            amount: 10000,
        },
        {
            name: "Jane Smith",
            invoiceNumber: "INV002",
            incomeHead: "Library Fees",
            date: "2025-01-05",
            amount: 5000,
        },
        {
            name: "Alice Johnson",
            invoiceNumber: "INV003",
            incomeHead: "Sports Fees",
            date: "2025-01-10",
            amount: 3000,
        },
        {
            name: "Bob Brown",
            invoiceNumber: "INV004",
            incomeHead: "Admission Fees",
            date: "2025-01-25",
            amount: 15000,
        },
        {
            name: "Charlie Davis",
            invoiceNumber: "INV005",
            incomeHead: "Transport Fees",
            date: "2025-02-15",
            amount: 8000,
        },
        {
            name: "Eva Green",
            invoiceNumber: "INV006",
            incomeHead: "Exam Fees",
            date: "2025-02-20",
            amount: 2000,
        },
        {
            name: "Frank White",
            invoiceNumber: "INV007",
            incomeHead: "Hostel Fees",
            date: "2025-02-10",
            amount: 12000,
        },
        {
            name: "Grace Black",
            invoiceNumber: "INV008",
            incomeHead: "Lab Fees",
            date: "2025-02-05",
            amount: 4000,
        },
        {
            name: "Henry Blue",
            invoiceNumber: "INV009",
            incomeHead: "Uniform Fees",
            date: "2025-03-01",
            amount: 2500,
        },
        {
            name: "Ivy Yellow",
            invoiceNumber: "INV010",
            incomeHead: "Miscellaneous Fees",
            date: "2025-03-03",
            amount: 1000,
        },
        {
            name: "Ivy Red",
            invoiceNumber: "INV010",
            incomeHead: "Miscellaneous Fees",
            date: "2025-03-15",
            amount: 1000,
        },
    ];

    // Handle date range selection
    const handleDateRangeChange = (e) => {
        const range = e.target.value;
        setSelectedDateRange(range);
        filterIncomeReports(range, searchQuery);
    };

    // Handle search input change
    const handleSearchChange = (e) => {
        const query = e.target.value;
        setSearchQuery(query);
        filterIncomeReports(selectedDateRange, query);
    };

    // Filter income reports based on date range and search query
    const filterIncomeReports = (dateRange, query) => {
        let filtered = incomeReports;

        // Filter by date range
        if (dateRange) {
            const today = new Date();
            const startDate = new Date();

            switch (dateRange) {
                case "today":
                    startDate.setHours(0, 0, 0, 0);
                    break;
                case "thisWeek":
                    startDate.setDate(today.getDate() - today.getDay());
                    break;
                case "lastWeek":
                    startDate.setDate(today.getDate() - today.getDay() - 7);
                    break;
                case "thisMonth":
                    startDate.setDate(1);
                    break;
                case "lastMonth":
                    startDate.setMonth(today.getMonth() - 1, 1);
                    break;
                case "3Months":
                    startDate.setMonth(today.getMonth() - 3);
                    break;
                case "6Months":
                    startDate.setMonth(today.getMonth() - 6);
                    break;
                case "12Months":
                    startDate.setMonth(today.getMonth() - 12);
                    break;
                case "1Year":
                    startDate.setFullYear(today.getFullYear() - 1);
                    break;
                default:
                    break;
            }

            filtered = filtered.filter((report) => {
                const reportDate = new Date(report.date);
                return reportDate >= startDate;
            });
        }

        // Filter by search query
        if (query) {
            filtered = filtered.filter(
                (report) =>
                    report.name.toLowerCase().includes(query.toLowerCase()) ||
                    report.invoiceNumber.toLowerCase().includes(query.toLowerCase()) ||
                    report.incomeHead.toLowerCase().includes(query.toLowerCase())
            );
        }

        setFilteredIncomeReports(filtered);
    };

    // Calculate grand total
    const grandTotal = filteredIncomeReports.reduce((total, report) => total + report.amount, 0);

    // Breadcrumb items
    const breadcrumbItems = [
        { title: "Reports", link: "/" },
        { title: "Finance Reports", link: "/" },
        { title: "Income Report", link: "/admin/income-report" },
    ];

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <Breadcrumbs title="Income Report" breadcrumbItems={breadcrumbItems} />
                    <Row>
                        <Col lg={12}>
                            <Card>
                                <CardHeader className="d-flex justify-content-between align-items-center flex-wrap">
                                    <h4 className="card-title mb-0 flex-grow-1">Income Report</h4>
                                </CardHeader>
                                <CardBody>
                                    <Row>
                                        {/* Date Range Select Input */}
                                        <Col lg={3}>
                                            <Label>Date Range</Label>
                                            <Input
                                                type="select"
                                                value={selectedDateRange}
                                                onChange={handleDateRangeChange}
                                            >
                                                <option value="">Select Date Range</option>
                                                <option value="today">Today</option>
                                                <option value="thisWeek">This Week</option>
                                                <option value="lastWeek">Last Week</option>
                                                <option value="thisMonth">This Month</option>
                                                <option value="lastMonth">Last Month</option>
                                                <option value="3Months">Last 3 Months</option>
                                                <option value="6Months">Last 6 Months</option>
                                                <option value="12Months">Last 12 Months</option>
                                                <option value="1Year">Last 1 Year</option>
                                            </Input>
                                        </Col>

                                        {/* Search Input */}
                                        <Col lg={6}>
                                            <Label>Search</Label>
                                            <Input
                                                type="text"
                                                placeholder="Search by Name, Invoice Number, or Income Head"
                                                value={searchQuery}
                                                onChange={handleSearchChange}
                                            />
                                        </Col>

                                        {/* Search Button */}
                                        <Col lg={3} className="d-flex align-items-end">
                                            <Button color="primary" onClick={() => filterIncomeReports(selectedDateRange, searchQuery)}>
                                                Search
                                            </Button>
                                        </Col>
                                    </Row>

                                    {/* Income Report Table */}
                                    <Row className="mt-4">
                                        <Col lg={12}>
                                            {filteredIncomeReports.length > 0 ? (
                                                <Table striped>
                                                    <thead>
                                                        <tr>
                                                            <th>SI No</th>
                                                            <th>Name</th>
                                                            <th>Invoice Number</th>
                                                            <th>Income Head</th>
                                                            <th>Date</th>
                                                            <th>Amount</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {filteredIncomeReports.map((report, index) => (
                                                            <tr key={report.siNo}>
                                                                {/* Dynamically assign SI No based on the filtered list index */}
                                                                <td>{index + 1}</td>
                                                                <td>{report.name}</td>
                                                                <td>{report.invoiceNumber}</td>
                                                                <td>{report.incomeHead}</td>
                                                                <td>{report.date}</td>
                                                                <td>{report.amount}</td>
                                                            </tr>
                                                        ))}
                                                        {/* Grand Totals Row */}
                                                        <tr style={{ fontWeight: "bold" }}>
                                                            <td colSpan="5">Grand Total</td>
                                                            <td>{grandTotal}</td>
                                                        </tr>
                                                    </tbody>
                                                </Table>
                                            ) : (
                                                <div className="text-center">
                                                    <p>No Record Found</p>
                                                </div>
                                            )}
                                        </Col>
                                    </Row>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>
        </React.Fragment>
    );
};

export default IncomeReport;